import { useState, useEffect, useRef } from 'react';
import { useScore, Member } from '../../../context/ScoreContext';

const FONT_HUD  = "'Orbitron', monospace";
const FONT_DECO = "'Cinzel', serif";
const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";

type Phase = 'enter' | 'mystery' | 'reveal-black' | 'reveal-white' | 'suspense' | 'winner' | 'champion';

// ── Count-up hook ─────────────────────────────────────────────────────────────
function useCountUp(target: number, active: boolean, duration = 1500) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) { setVal(0); return; }
    const start = performance.now();
    const animate = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [active, target, duration]);
  return val;
}

// ── Roulette number (spin before settle) ─────────────────────────────────────
function RouletteNumber({ active, target }: { active: boolean; target: number }) {
  const [display, setDisplay] = useState<string>('??');
  const raf = useRef<number>(0);

  useEffect(() => {
    if (!active) { setDisplay('??'); return; }
    const totalMs = 1600;
    const spinMs   = totalMs * 0.65;
    const startTs  = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTs;
      if (elapsed < spinMs) {
        const rnd = Math.floor(Math.random() * Math.max(target + 80, 99));
        setDisplay(String(rnd));
        raf.current = requestAnimationFrame(tick);
      } else {
        const p = Math.min((elapsed - spinMs) / (totalMs - spinMs), 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(String(Math.round(target * eased)));
        if (p < 1) {
          raf.current = requestAnimationFrame(tick);
        } else {
          setDisplay(String(target));
        }
      }
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [active, target]);

  return <>{display}</>;
}

// ── Particle field ────────────────────────────────────────────────────────────
interface Particle { id: number; x: number; delay: number; size: number; color: string; duration: number; xDrift: number; shape: 'circle' | 'diamond'; }

function ParticleField({ winner }: { winner: 'black' | 'white' | 'tie' }) {
  const colors = winner === 'black'
    ? ['#d4a843', '#f0d060', '#e8c040', '#fff7d0', '#ffd700', '#d4a843']
    : winner === 'white'
    ? ['#e0e0e0', '#ffffff', '#c8c8c8', '#f0f0f0', '#d8d8d8', '#fff']
    : ['#d4a843', '#e0e0e0', '#f0d060', '#c8c8c8', '#fff', '#ffd700'];

  const particles: Particle[] = Array.from({ length: 80 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 2.5,
    size: Math.random() * 10 + 3,
    color: colors[i % colors.length],
    duration: 1.6 + Math.random() * 2.2,
    xDrift: (Math.random() - 0.5) * 200,
    shape: Math.random() > 0.5 ? 'circle' : 'diamond',
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {particles.map(p => (
        <div key={p.id}
          className="absolute"
          style={{
            left: `${p.x}%`,
            bottom: -10,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: p.shape === 'circle' ? '50%' : '2px',
            transform: p.shape === 'diamond' ? 'rotate(45deg)' : undefined,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            '--x-drift': `${p.xDrift}px`,
            animation: `stlParticle ${p.duration}s ${p.delay}s cubic-bezier(0.2,0.8,0.4,1) forwards`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

// ── Score card (vertical layout) ──────────────────────────────────────────────
function ScoreCard({
  team, score, phase,
}: {
  team: 'black' | 'white';
  score: number;
  phase: Phase;
}) {
  const color     = team === 'black' ? '#d4a843' : '#e2e2e2';
  const rgb       = team === 'black' ? '212,168,67' : '226,226,226';
  const label     = team === 'black' ? '黑隊' : '白隊';
  const sublabel  = team === 'black' ? 'BLACK TEAM' : 'WHITE TEAM';
  const icon      = team === 'black' ? 'ri-shield-fill' : 'ri-sword-fill';
  const slideDir  = team === 'black' ? 'stlSlideRight' : 'stlSlideLeft';

  const isMystery  = phase === 'enter';
  const showMyster = phase === 'mystery';
  const isReveal   = (team === 'black' && (phase === 'reveal-black' || phase === 'reveal-white' || phase === 'suspense' || phase === 'winner' || phase === 'champion'))
                  || (team === 'white' && (phase === 'reveal-white' || phase === 'suspense' || phase === 'winner' || phase === 'champion'));
  const isVisible  = phase !== 'enter';

  return (
    <div
      className="relative rounded-2xl overflow-hidden w-full"
      style={{
        background: `linear-gradient(140deg, rgba(${rgb},0.10), rgba(${rgb},0.03))`,
        border: `1.5px solid rgba(${rgb},0.25)`,
        opacity: isVisible ? 1 : 0,
        animation: isVisible ? `${slideDir} 0.65s cubic-bezier(0.34,1.56,0.64,1) forwards` : 'none',
      }}
    >
      {/* Top glow */}
      <div className="absolute top-0 left-0 right-0 h-0.5"
        style={{ background: `linear-gradient(90deg, transparent, rgba(${rgb},0.6), transparent)` }} />

      <div className="px-5 py-4 flex items-center gap-4">
        {/* Icon */}
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{
              background: `radial-gradient(circle at 35% 35%, rgba(${rgb},0.25), rgba(${rgb},0.06))`,
              border: `2px solid rgba(${rgb},0.35)`,
            }}>
            <i className={`${icon} text-xl`} style={{ color }} />
          </div>
        </div>

        {/* Name */}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black tracking-[0.35em] uppercase leading-none"
            style={{ color: `rgba(${rgb},0.55)`, fontFamily: FONT_DECO }}>{sublabel}</p>
          <p className="text-xl font-black mt-0.5 leading-tight"
            style={{ color, fontFamily: FONT_BODY }}>{label}</p>
        </div>

        {/* Score */}
        <div className="shrink-0 text-right">
          <div className="font-black tabular-nums leading-none"
            style={{
              fontSize: 'clamp(2.2rem, 10vw, 3.8rem)',
              fontFamily: FONT_HUD,
              color,
              textShadow: isReveal
                ? `0 0 24px rgba(${rgb},0.7), 0 0 50px rgba(${rgb},0.3)`
                : showMyster
                ? `0 0 10px rgba(${rgb},0.4)`
                : 'none',
              filter: showMyster && !isReveal ? 'blur(1px)' : 'none',
              transition: 'filter 0.3s, text-shadow 0.3s',
            }}>
            {showMyster && !isReveal ? (
              <span style={{ letterSpacing: '0.1em', opacity: 0.5 }}>?</span>
            ) : (
              <RouletteNumber active={isReveal} target={score} />
            )}
          </div>
          <p className="text-[9px] tracking-widest mt-0.5"
            style={{ color: `rgba(${rgb},0.4)`, fontFamily: FONT_BODY }}>
            {isReveal ? '總積分' : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Suspense countdown ────────────────────────────────────────────────────────
function SuspenseDots({ visible }: { visible: boolean }) {
  return (
    <div
      className="flex items-center justify-center gap-2"
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.4s ease',
      }}
    >
      {[0, 150, 300].map(d => (
        <div key={d} className="w-2 h-2 rounded-full"
          style={{
            background: '#d4a843',
            animation: `stlPulse 0.8s ${d}ms ease-in-out infinite`,
          }} />
      ))}
    </div>
  );
}

// ── Winner banner ─────────────────────────────────────────────────────────────
function WinnerBanner({
  winner, blackTotal, whiteTotal, visible,
}: {
  winner: 'black' | 'white' | 'tie';
  blackTotal: number;
  whiteTotal: number;
  visible: boolean;
}) {
  const color = winner === 'black' ? '#d4a843' : winner === 'white' ? '#e2e2e2' : '#a0a0a0';
  const rgb   = winner === 'black' ? '212,168,67' : winner === 'white' ? '226,226,226' : '160,160,160';
  const label = winner === 'black' ? '黑隊' : winner === 'white' ? '白隊' : '';
  const diff  = Math.abs(blackTotal - whiteTotal);

  return (
    <div
      className="w-full rounded-2xl relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, rgba(${rgb},0.16), rgba(${rgb},0.05))`,
        border: `2px solid rgba(${rgb},0.5)`,
        boxShadow: `0 0 40px rgba(${rgb},0.25), 0 0 80px rgba(${rgb},0.1)`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1) translateY(0)' : 'scale(0.7) translateY(20px)',
        transition: 'opacity 0.7s cubic-bezier(0.34,1.56,0.64,1), transform 0.7s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      {/* Top shimmer */}
      <div className="absolute top-0 left-0 right-0 h-0.5"
        style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
      {/* Scan animation */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: `linear-gradient(90deg, transparent, rgba(${rgb},0.1), transparent)`, animation: 'stlShimmer 2s linear infinite' }} />

      <div className="relative z-10 px-6 py-5 text-center">
        {winner !== 'tie' ? (
          <>
            <div className="flex items-center justify-center gap-2 mb-1">
              <i className="ri-vip-crown-2-fill text-xl"
                style={{ color: '#ffd700', filter: 'drop-shadow(0 0 10px rgba(255,215,0,0.8))', animation: 'stlCrownBounce 1.2s ease-in-out infinite' }} />
              <p className="text-[10px] font-black tracking-[0.5em] uppercase"
                style={{ color: `rgba(${rgb},0.65)`, fontFamily: FONT_DECO }}>WINNER</p>
              <i className="ri-vip-crown-2-fill text-xl"
                style={{ color: '#ffd700', filter: 'drop-shadow(0 0 10px rgba(255,215,0,0.8))', animation: 'stlCrownBounce 1.2s 0.3s ease-in-out infinite' }} />
            </div>
            <p className="font-black text-3xl sm:text-4xl leading-tight"
              style={{ color, fontFamily: FONT_BODY, textShadow: `0 0 30px rgba(${rgb},0.7)` }}>
              {label}
              <span className="ml-2 text-2xl sm:text-3xl" style={{ opacity: 0.8 }}>勝出！</span>
            </p>
            <p className="text-xs mt-1.5" style={{ color: `rgba(${rgb},0.5)`, fontFamily: FONT_BODY }}>
              領先 {diff} 分
            </p>
          </>
        ) : (
          <>
            <p className="font-black text-2xl sm:text-3xl"
              style={{ color: '#a0a0a0', fontFamily: FONT_BODY }}>
              旗鼓相當！
            </p>
            <p className="text-sm mt-1" style={{ color: '#555', fontFamily: FONT_BODY }}>平手 · 雙方積分相同</p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Champion Card ─────────────────────────────────────────────────────────────
function ChampionCard({ member, points, visible }: { member: Member | null; points: number; visible: boolean }) {
  if (!member) return null;
  const color    = member.team === 'black' ? '#d4a843' : '#e2e2e2';
  const rgb      = member.team === 'black' ? '212,168,67' : '226,226,226';
  const teamLabel = member.team === 'black' ? '黑隊' : '白隊';

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(140deg, rgba(${rgb},0.10), #060614)`,
        border: `1.5px solid rgba(${rgb},0.3)`,
        boxShadow: `0 0 30px rgba(${rgb},0.18)`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.7s ease-out, transform 0.8s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, rgba(${rgb},0.6), transparent)` }} />

      <div className="px-5 py-4 flex items-center gap-4">
        <div className="shrink-0">
          {member.avatarUrl ? (
            <img src={member.avatarUrl} alt={member.name}
              className="w-14 h-14 rounded-full object-cover object-top"
              style={{ border: `3px solid rgba(${rgb},0.6)`, boxShadow: `0 0 18px rgba(${rgb},0.4)` }} />
          ) : (
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-black"
              style={{ background: `rgba(${rgb},0.18)`, color, border: `3px solid rgba(${rgb},0.5)`, fontFamily: FONT_BODY, boxShadow: `0 0 18px rgba(${rgb},0.35)` }}>
              {member.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-black tracking-[0.4em] uppercase mb-0.5"
            style={{ color: `rgba(${rgb},0.55)`, fontFamily: FONT_DECO }}>✦ 個人 MVP ✦</p>
          <p className="text-white text-lg font-black leading-tight truncate"
            style={{ fontFamily: FONT_BODY }}>{member.name}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[10px] px-2 py-0.5 rounded-full font-black"
              style={{ background: `rgba(${rgb},0.15)`, color, border: `1px solid rgba(${rgb},0.35)`, fontFamily: FONT_DECO }}>
              {teamLabel}
            </span>
            <span className="text-sm font-black tabular-nums" style={{ color, fontFamily: FONT_HUD }}>+{points}</span>
            <span className="text-xs" style={{ color: `rgba(${rgb},0.5)`, fontFamily: FONT_BODY }}>個人積分</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SettlementOverlay() {
  const { scheduleState, scores, members, setSettlementActive } = useScore();
  const { settlementActive } = scheduleState;

  const [phase, setPhase]       = useState<Phase>('enter');
  const [champVisible, setChampVisible] = useState(false);
  const [exiting, setExiting]   = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const blackTotal = scores.black.battle + scores.black.chemistry + scores.black.dreamCoins;
  const whiteTotal = scores.white.battle + scores.white.chemistry + scores.white.dreamCoins;
  const winner: 'black' | 'white' | 'tie' =
    blackTotal > whiteTotal ? 'black' : whiteTotal > blackTotal ? 'white' : 'tie';

  const memberPoints = scores.history.reduce<Record<string, number>>((acc, ev) => {
    if (ev.memberId && ev.memberId !== 'system' && ev.points > 0)
      acc[ev.memberId] = (acc[ev.memberId] ?? 0) + ev.points;
    return acc;
  }, {});
  const champEntry  = Object.entries(memberPoints).sort((a, b) => b[1] - a[1])[0];
  const champMember = champEntry ? members.find(m => m.id === champEntry[0]) ?? null : null;
  const champPoints = champEntry ? champEntry[1] : 0;

  // Phase timeline
  useEffect(() => {
    if (!settlementActive) return;
    setPhase('enter');
    setChampVisible(false);
    setExiting(false);
    timerRef.current.forEach(clearTimeout);
    timerRef.current = [];

    const t = (fn: () => void, ms: number) => {
      const id = setTimeout(fn, ms);
      timerRef.current.push(id);
    };

    // Mystery — cards slide in showing "?"
    t(() => setPhase('mystery'), 700);
    // Reveal black score
    t(() => setPhase('reveal-black'), 2300);
    // Reveal white score
    t(() => setPhase('reveal-white'), 4000);
    // Suspense dots
    t(() => setPhase('suspense'), 5600);
    // Winner revealed
    t(() => setPhase('winner'), 6500);
    // Champion card
    t(() => { setPhase('champion'); setChampVisible(true); }, 8000);

    return () => { timerRef.current.forEach(clearTimeout); timerRef.current = []; };
  }, [settlementActive]);

  const handleClose = () => {
    setExiting(true);
    setTimeout(() => {
      setSettlementActive(false);
      setExiting(false);
    }, 500);
  };

  if (!settlementActive && !exiting) return null;

  const winnerColor = winner === 'black' ? '#d4a843' : winner === 'white' ? '#e2e2e2' : '#a0a0a0';
  const winnerRgb   = winner === 'black' ? '212,168,67' : winner === 'white' ? '226,226,226' : '160,160,160';
  const showWinner  = phase === 'winner' || phase === 'champion';
  const showSuspense = phase === 'suspense';

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col overflow-hidden"
      style={{
        background: 'rgba(3,3,12,0.97)',
        backdropFilter: 'blur(14px)',
        opacity: exiting ? 0 : 1,
        transition: exiting ? 'opacity 0.5s ease-out' : 'opacity 0.4s ease-in',
      }}
    >
      {/* Particles */}
      {showWinner && winner !== 'tie' && <ParticleField winner={winner} />}

      {/* Dynamic BG glow when winner revealed */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: showWinner
            ? `radial-gradient(ellipse 70% 50% at 50% 100%, rgba(${winnerRgb},0.12), transparent)`
            : 'none',
          transition: 'background 1s ease',
        }}
      />

      {/* Grid dots */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '28px 28px', opacity: 0.8 }} />

      {/* Top line */}
      <div className="absolute top-0 left-0 right-0 h-px z-10"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.5), transparent)' }} />

      {/* Close */}
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all active:scale-90"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#666' }}
      >
        <i className="ri-close-line text-lg" />
      </button>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 py-10 gap-5 overflow-y-auto">

        {/* Title */}
        <div
          className="text-center"
          style={{
            opacity: phase !== 'enter' ? 1 : 0,
            transform: phase !== 'enter' ? 'translateY(0)' : 'translateY(-24px)',
            transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="h-px w-10" style={{ background: 'linear-gradient(90deg, transparent, #d4a84360)' }} />
            <span className="text-[9px] font-black tracking-[0.55em] uppercase"
              style={{ color: '#d4a84370', fontFamily: FONT_DECO }}>FINAL SCORE</span>
            <div className="h-px w-10" style={{ background: 'linear-gradient(90deg, #d4a84360, transparent)' }} />
          </div>
          <h1
            className="text-4xl font-black text-white"
            style={{ fontFamily: FONT_DECO, letterSpacing: '0.15em', textShadow: '0 0 40px rgba(212,168,67,0.3)' }}
          >
            決算時刻
          </h1>
        </div>

        {/* Reveal order hint */}
        {(phase === 'mystery' || phase === 'reveal-black') && (
          <p
            className="text-[10px] tracking-[0.3em]"
            style={{
              color: '#2a3a5a',
              fontFamily: FONT_BODY,
              animation: 'stlBlink 1.2s ease-in-out infinite',
            }}
          >
            {phase === 'mystery' ? '正在計算積分⋯' : '黑隊積分揭曉中⋯'}
          </p>
        )}
        {phase === 'reveal-white' && (
          <p
            className="text-[10px] tracking-[0.3em]"
            style={{ color: '#2a3a5a', fontFamily: FONT_BODY, animation: 'stlBlink 1.2s ease-in-out infinite' }}
          >
            白隊積分揭曉中⋯
          </p>
        )}

        {/* Score cards — vertical stack */}
        <div className="w-full max-w-sm flex flex-col gap-3">
          <ScoreCard team="black" score={blackTotal} phase={phase} />
          {/* VS divider */}
          <div className="flex items-center gap-3">
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.2))' }} />
            <span className="text-xs font-black" style={{ color: '#1e2a3a', fontFamily: FONT_DECO }}>VS</span>
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, rgba(212,168,67,0.2), transparent)' }} />
          </div>
          <ScoreCard team="white" score={whiteTotal} phase={phase} />
        </div>

        {/* Suspense dots */}
        <SuspenseDots visible={showSuspense} />

        {/* Winner banner */}
        {(phase === 'winner' || phase === 'champion' || phase === 'suspense') && (
          <div className="w-full max-w-sm">
            <WinnerBanner
              winner={winner}
              blackTotal={blackTotal}
              whiteTotal={whiteTotal}
              visible={showWinner}
            />
          </div>
        )}

        {/* Champion */}
        {champMember && (phase === 'champion' || phase === 'winner') && (
          <div className="w-full max-w-sm">
            <ChampionCard member={champMember} points={champPoints} visible={champVisible} />
          </div>
        )}

        {/* Close hint */}
        <button
          onClick={handleClose}
          className="px-8 py-2.5 rounded-full text-sm font-bold cursor-pointer transition-all active:scale-95 whitespace-nowrap"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#444',
            fontFamily: FONT_BODY,
            opacity: phase === 'champion' ? 1 : 0,
            transition: 'opacity 0.6s ease',
          }}
        >
          關閉結算畫面
        </button>
      </div>

      <style>{`
        @keyframes stlSlideRight {
          from { opacity:0; transform: translateX(-50px) scale(0.9); }
          to   { opacity:1; transform: translateX(0) scale(1); }
        }
        @keyframes stlSlideLeft {
          from { opacity:0; transform: translateX(50px) scale(0.9); }
          to   { opacity:1; transform: translateX(0) scale(1); }
        }
        @keyframes stlCrownBounce {
          0%,100% { transform: translateY(0) rotate(-6deg); }
          50%     { transform: translateY(-5px) rotate(6deg); }
        }
        @keyframes stlShimmer {
          from { transform: translateX(-100%); }
          to   { transform: translateX(400%); }
        }
        @keyframes stlParticle {
          0%   { transform: translateY(0) translateX(0) scale(1); opacity:1; }
          80%  { opacity:0.6; }
          100% { transform: translateY(-90vh) translateX(var(--x-drift,0px)) scale(0.15); opacity:0; }
        }
        @keyframes stlPulse {
          0%,100% { transform: scale(0.7); opacity:0.35; }
          50%     { transform: scale(1.5); opacity:1; }
        }
        @keyframes stlBlink {
          0%,100% { opacity:0.4; }
          50%     { opacity:1; }
        }
      `}</style>
    </div>
  );
}
